import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import {
  subscribeNodes,
  addNode,
  voteNode,
  verifyStoryPassword,
} from "../modules/firebaseAPI";
import "./ChatPage.css";
import ChatPageSidebar from "../components/Sidebar/ChatPageSidebar";
import CommentSection from "../modules/Chat/CommentSection";
import PasswordPrompt from "../modules/PasswordPrompt"; // ✅ 追加
import AffiliateLinks from "../modules/amazonAffiliate/AffiliateLinks";
import Seo from "../components/Seo";

// 投票設定
const VoteSettings = ({ story, userId, setStory }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newVoteMethod, setNewVoteMethod] = useState(story.voteMethod);
  const [newVoteTime, setNewVoteTime] = useState(story.voteTime);

  const handleSave = async () => {
    const storyRef = doc(db, "stories", story.id);
    try {
      await updateDoc(storyRef, {
        voteMethod: newVoteMethod,
        voteTime: Number(newVoteTime),
        updatedAt: serverTimestamp(),
      });
      setStory((prev) => ({
        ...prev,
        voteMethod: newVoteMethod,
        voteTime: Number(newVoteTime),
      }));
      setIsEditing(false);
    } catch (err) {
      console.error("更新失敗:", err);
    }
  };

  if (story.createdBy !== userId) {
    return (
      <div>
        <p>
          <strong>投票方法:</strong> {story.voteMethod}
        </p>
        <p>
          <strong>投票時間:</strong> {story.voteTime}分
        </p>
      </div>
    );
  }

  return (
    <div>
      {!isEditing ? (
        <>
          <p>
            <strong>投票方法:</strong>{" "}
            {(() => {
              switch (story.voteMethod) {
                case "author":
                  return "スレ主が選ぶ";
                case "timed":
                  return "ランダム採用";
                case "vote":
                  return "多数決";
                default:
                  return "不明";
              }
            })()}
          </p>
          {newVoteMethod !== "author" && (
            <p>
              <strong>投票時間:</strong> {story.voteTime}分
            </p>
          )}
          <button onClick={() => setIsEditing(true)}>
            スレッドの設定を変更
          </button>
        </>
      ) : (
        <div>
          <label>
            投票方法:
            <select
              value={newVoteMethod}
              onChange={(e) => setNewVoteMethod(e.target.value)}
            >
              <option value="author">スレ主が選ぶ</option>
              <option value="timed">ランダム採用</option>
              <option value="vote">多数決</option>
            </select>
          </label>
          <br />

          {newVoteMethod !== "author" && (
            <label>
              投票時間(分):
              <input
                type="number"
                value={newVoteTime}
                onChange={(e) => setNewVoteTime(e.target.value)}
                min={1}
              />
            </label>
          )}
          <br />
          <button onClick={handleSave}>保存</button>
          <button onClick={() => setIsEditing(false)}>キャンセル</button>
        </div>
      )}
    </div>
  );
};

export default function ChatPageWrapper({ userId }) {
  const { storyId: paramStoryId, nodeId: paramNodeId } = useParams();
  const location = useLocation();

  const storyId = paramStoryId || location.state?.storyId;
  const nodeId = paramNodeId || location.state?.nodeId;

  const [nodes, setnodes] = useState([]);
  const [nodeData, setNodeData] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // nodes購読
  useEffect(() => {
    if (!storyId) return;
    const unsubscribe = subscribeNodes(storyId, setnodes);
    return () => unsubscribe();
  }, [storyId]);

  // story & node取得
  useEffect(() => {
    if (!storyId || !nodeId) return;

    const fetchData = async () => {
      try {
        const { getNode, getThread } = await import("../modules/firebaseAPI");
        const [node, fetchedStory] = await Promise.all([
          getNode(storyId, nodeId),
          getThread(storyId),
        ]);
        setNodeData(node);
        setStory(fetchedStory);
      } catch (error) {
        console.error("データ取得エラー:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [storyId, nodeId]);

  // 開始文初期化
  useEffect(() => {
    if (story && nodeData && !story.currentNodeContent) {
      const storyRef = doc(db, "stories", story.id);
      updateDoc(storyRef, {
        currentNodeContent: nodeData.content,
        updatedAt: serverTimestamp(),
      })
        .then(() => {
          setStory((prev) => ({
            ...prev,
            currentNodeContent: nodeData.content,
          }));
        })
        .catch((err) => console.error("開始文の初期化に失敗:", err));
    }
  }, [story, nodeData]);

  const handleaddNode = async (newNode) => {
    if (!story) return;
    await addNode(story.id, newNode);
  };

  const handleVote = async (nodeId) => {
    if (!story) return;
    await voteNode(story.id, nodeId, userId);
  };

  if (loading) return <div>チャット読み込み中...</div>;
  if (!nodeData || !story) return <div>データが存在しません</div>;

  // ✅ パスワード認証が必要な場合
  if (story.password && !accessGranted) {
    return (
      <PasswordPrompt
        onSubmit={async (inputPassword) => {


          try {
            const isValid = await verifyStoryPassword(story.id, inputPassword);

            if (isValid) {
              setAccessGranted(true);
              return true; // 成功時はtrueを返す
            } else {
              return false; // 失敗時はfalseを返す
            }
          } catch (err) {
            console.error("パスワード検証エラー:", err);
            return false; // エラー時もfalse
          }
        }}
      />
    );
  }

  return (
    <div className="fade-in" style={{ display: "flex" }}>
      {/* ハンバーガーボタン */}
      <button
        className="sidebar-toggle"
        onClick={() => {
          setSidebarOpen(!sidebarOpen);
        }}
      >
        {sidebarOpen ? "×" : "投票をみる"}
      </button>

      <ChatPage
        userId={userId}
        currentNode={nodeData}
        story={story}
        setStory={setStory}
      />
      <ChatPageSidebar
        className={`chat-sidebar ${sidebarOpen ? "open" : ""}`}
        currentNode={nodeData}
        nodes={nodes}
        setNodes={setnodes}
        onAddNode={handleaddNode}
        onVoteNode={handleVote}
        onResetNodes={() => setnodes([])}
        userId={userId}
        story={story}
        setStory={setStory}
        voteTime={story.voteTime}
        voteMethod={story.voteMethod}
      />
    </div>
  );
}

export const ChatPage = ({ userId, story, currentNode, setStory }) => {
  const { nodeId } = useParams();

  if (!currentNode || !story) return <div>データを取得中...</div>;

  return (
    <>
      {/* 🔹 SEO タグ */}
      <Seo
        title={story.title}
        description={story.title}
        image="/siteicon.PNG"
         url={`https://mind-threat.site/stories/${story.id}`}
        type="article"
      />

      <div className="chat-container">
        <div className="thread-info">
          <h1>{story.title}</h1>
          <p>
            <strong>開始文:</strong> {currentNode.content}
          </p>
          <p>
            <strong>カテゴリ:</strong> {story.category}
          </p>
          <p>
            <strong>スレッドタイプ:</strong>{" "}
            {(() => {
              switch (story.threadType) {
                case "itinerary":
                  return "マインドマップ型";
                case "story":
                  return "ストーリー型";
                default:
                  return story.threadType;
              }
            })()}
          </p>

          <VoteSettings story={story} userId={userId} setStory={setStory} />
        </div>

        <div className="affiliate-group home-section">
          <AffiliateLinks />
        </div>

        <CommentSection nodeId={nodeId} userId={userId} />
      </div>
    </>
  );
};
